import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { fetchTraderDetail, traderName, waNumber } from '../../lib/consoleTraders';
import { markEventHandled } from '../../lib/consoleNotifications';
import { quotationBoardStatus } from '../../lib/consoleQuotations';
import { STATUS } from './ConsoleQuotations';

// One trader, everything about them, without leaving the new console (owner,
// 2026-10-11): contact, every request, every conversation, every alert they
// produced (with the same "handled" control as the inbox) and their direct
// purchases. Read-only except for marking alerts handled.

const when = (iso, isAr) => (iso ? new Date(iso).toLocaleString(isAr ? 'ar-SA' : 'en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');
const KIND = {
  direct: { ar: 'طلب عرض سعر', en: 'Quote request' },
  factory: { ar: 'طلب لمصنع', en: 'Factory request' },
  managed: { ar: 'مُدار', en: 'Managed' },
  idea_to_product: { ar: 'صنّع فكرتك', en: 'Idea to product' },
};

function Section({ title, count, children }) {
  return (
    <div className="ac-card" style={{ padding: 16, marginBottom: 14, border: '1px solid var(--ac-line)' }}>
      <div className="ac-card-title" style={{ marginBottom: 10 }}>
        {title}{count != null && <span style={{ color: 'var(--ac-faint)', fontWeight: 500, marginInlineStart: 6 }}>{count}</span>}
      </div>
      {children}
    </div>
  );
}

export default function ConsoleTraderDetail({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const { id } = useParams();
  const [d, setD] = useState(undefined);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    setD(undefined);
    fetchTraderDetail(id).then(setD).catch((e) => { setD(null); setError(e.message); });
  }, [id]);

  const toggle = async (ev) => {
    setBusy(ev.id);
    try {
      await markEventHandled(ev.id, !ev.handled);
      setD((x) => ({ ...x, events: x.events.map((e) => (e.id === ev.id ? { ...e, handled: !ev.handled } : e)) }));
    } catch (e) {
      alert(isAr ? `تعذّر الحفظ: ${e.message}` : `Could not save: ${e.message}`);
    } finally { setBusy(null); }
  };

  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
  const empty = (ar, en) => <div className="ac-placeholder">{isAr ? ar : en}</div>;
  const p = d && d.profile;
  const wa = p && waNumber(p.whatsapp || p.phone);

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="traders">
      <div className="ac-page" style={{ fontFamily: font, maxWidth: 980 }}>
        <button className="ac-back" onClick={() => nav('/admin2/traders')}>{isAr ? '→ التجار' : '← Traders'}</button>

        {d === undefined ? <div className="ac-skel" style={{ height: 160 }} /> : !d ? (
          <div className="ac-placeholder" style={{ color: error ? 'var(--ac-danger)' : undefined }}>
            {error || (isAr ? 'لم يُعثر على هذا التاجر.' : 'Trader not found.')}
          </div>
        ) : (
          <>
            <div className="ac-page-head">
              <div>
                <h1 className="ac-h1">{traderName(p)}</h1>
                <p className="ac-sub">
                  {[p.company_name && p.full_name ? p.full_name : '', p.city, p.country].filter(Boolean).join(' · ') || (isAr ? 'تاجر' : 'Trader')}
                  {p.role !== 'buyer' && <span className="ac-badge warn" style={{ marginInlineStart: 8 }}>{p.role}</span>}
                </p>
              </div>
              {wa && (
                <a className="ac-btn ac-btn-sm" href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer"
                  style={{ background: '#25D366', color: '#fff', borderColor: '#25D366', textDecoration: 'none' }}>
                  {isAr ? 'راسل على واتساب' : 'WhatsApp'}
                </a>
              )}
            </div>

            <Section title={isAr ? 'معلومات التاجر' : 'Trader details'}>
              {[
                [isAr ? 'الجوال' : 'Phone', p.phone, true],
                [isAr ? 'واتساب' : 'WhatsApp', p.whatsapp, true],
                [isAr ? 'الإيميل' : 'Email', p.email, true],
                [isAr ? 'المدينة' : 'City', [p.city, p.country].filter(Boolean).join('، ')],
                [isAr ? 'التسجيل' : 'Joined', when(p.created_at, isAr)],
                [isAr ? 'آخر دخول' : 'Last seen', p.last_platform_at ? `${when(p.last_platform_at, isAr)}${p.last_platform ? ` · ${p.last_platform}` : ''}` : '—'],
              ].map(([k, v, ltr]) => (
                <div className="ac-kv" key={k}>
                  <span className="ac-kv-k">{k}</span>
                  <span className="ac-kv-v" dir={ltr ? 'ltr' : undefined} style={ltr ? { textAlign: isAr ? 'right' : 'left' } : undefined}>{v || '—'}</span>
                </div>
              ))}
            </Section>

            <Section title={isAr ? 'النشاط والتنبيهات' : 'Activity & alerts'} count={d.events.length}>
              {d.events.length === 0 ? empty('لا تنبيهات مسجّلة لهذا التاجر بعد.', 'No alerts recorded for this trader yet.') : d.events.map((ev) => (
                <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--ac-line)', opacity: ev.handled ? 0.6 : 1 }}>
                  <div role={ev.link ? 'button' : undefined} onClick={() => ev.link && nav(ev.link)}
                    style={{ flex: 1, minWidth: 0, cursor: ev.link ? 'pointer' : 'default' }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ac-ink)' }}>{isAr ? ev.title_ar : ev.title_en}</div>
                    {ev.sub && <div style={{ fontSize: 12, color: 'var(--ac-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.sub}</div>}
                  </div>
                  <span style={{ fontSize: 11.5, color: 'var(--ac-faint)', whiteSpace: 'nowrap' }}>{when(ev.at, isAr)}</span>
                  <button className={`ac-btn ac-btn-sm${ev.handled ? '' : ' ac-btn-primary'}`} disabled={busy === ev.id} onClick={() => toggle(ev)}>
                    {ev.handled ? (isAr ? 'تراجع' : 'Undo') : (isAr ? 'تم التعامل' : 'Handled')}
                  </button>
                </div>
              ))}
            </Section>

            <Section title={isAr ? 'الطلبات' : 'Requests'} count={d.requests.length}>
              {d.requests.length === 0 ? empty('لم يرفع طلبات.', 'No requests.') : d.requests.map((r) => {
                const st = STATUS[quotationBoardStatus(r, r.status === 'offers_received')];
                return (
                  <div key={r.id} role="button" onClick={() => nav(`/admin2/requests/${r.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--ac-line)', cursor: 'pointer' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ac-ink)' }}>
                        {(isAr ? (r.title_ar || r.title_en) : (r.title_en || r.title_ar)) || (isAr ? 'طلب' : 'Request')}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ac-faint)' }}>
                        {[r.request_ref, KIND[r.request_kind] ? (isAr ? KIND[r.request_kind].ar : KIND[r.request_kind].en) : r.request_kind,
                          r.quantity ? `${r.quantity}${r.unit ? ' ' + r.unit : ''}` : ''].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    {st && <span className={`ac-badge ${st.cls}`}>{isAr ? st.ar : st.en}</span>}
                    <span style={{ fontSize: 11.5, color: 'var(--ac-faint)', whiteSpace: 'nowrap' }}>{when(r.created_at, isAr)}</span>
                  </div>
                );
              })}
            </Section>

            <Section title={isAr ? 'المحادثات' : 'Conversations'} count={d.conversations.length}>
              {d.conversations.length === 0 ? empty('لم يراسل أي مصنع أو مورد.', 'Has not messaged any factory or supplier.') : d.conversations.map((c) => (
                <div key={`${c.channel}:${c.counterpart_id}`} role="button"
                  onClick={() => nav(`/admin2/conversations?${new URLSearchParams({ channel: c.channel, trader: c.trader_id, counterpart: c.counterpart_id })}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--ac-line)', cursor: 'pointer' }}>
                  <span className={`ac-badge ${c.channel === 'factory' ? 'warn' : 'ok'}`}>{c.channel === 'factory' ? (isAr ? 'مصنع' : 'Factory') : (isAr ? 'مورد' : 'Supplier')}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ac-ink)' }}>{c.counterpart_name || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--ac-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.last_preview}</div>
                  </div>
                  <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>{c.message_count}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--ac-faint)', whiteSpace: 'nowrap' }}>{when(c.last_at, isAr)}</span>
                </div>
              ))}
            </Section>

            <Section title={isAr ? 'طلبات الشراء المباشر' : 'Direct purchases'} count={d.purchases.length}>
              {d.purchases.length === 0 ? empty('لا طلبات شراء مباشر.', 'No direct purchases.') : d.purchases.map((pr) => (
                <div key={pr.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--ac-line)' }}>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--ac-ink)' }}>
                    {pr.productName || (isAr ? 'منتج' : 'Product')}{pr.quantity ? ` × ${pr.quantity}` : ''}
                  </div>
                  <span className="ac-badge neutral">{pr.status}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--ac-faint)', whiteSpace: 'nowrap' }}>{when(pr.created_at, isAr)}</span>
                </div>
              ))}
            </Section>
          </>
        )}
      </div>
    </ConsoleShell>
  );
}
